import { useEffect, useState, useCallback } from 'react'
import { Activity } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useActivityStore } from '@/stores/activity-store'
import { useAuthStore } from '@/stores/auth-store'
import { ActivityRow } from '@/components/activity/ActivityRow'
import { exportBidActivityCsv } from '@/lib/activity-api'
import { api } from '@/lib/api'
import { toast } from 'sonner'
import { format, isToday, isYesterday } from 'date-fns'

// ===================
// Types
// ===================

interface BidMember {
  id: string
  bidId: string
  userId: string
  role: 'owner' | 'contributor'
  email: string
  fullName: string | null
  avatarUrl: string | null
  createdAt: string
}

interface ActivitySectionProps {
  bidId: string
}

// ===================
// Constants
// ===================

const TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Bid', value: 'bid' },
  { label: 'Strategy', value: 'strategy' },
  { label: 'Documents', value: 'documents' },
  { label: 'Requirements & Analysis', value: 'requirements' },
  { label: 'Drafting & Structure', value: 'drafting' },
  { label: 'Collaboration', value: 'collaboration' },
  { label: 'Review', value: 'review' },
]

const DATE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
]

// ===================
// Helpers
// ===================

function getDayLabel(dateStr: string): string {
  const d = new Date(dateStr)
  if (isToday(d)) return 'Today'
  if (isYesterday(d)) return 'Yesterday'
  return format(d, 'd MMM')
}

function getDayKey(dateStr: string): string {
  const d = new Date(dateStr)
  return format(d, 'yyyy-MM-dd')
}

function getFromDate(preset: string): string {
  const now = new Date()
  if (preset === 'today') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    return start.toISOString()
  }
  if (preset === '7d') {
    const d = new Date(now)
    d.setDate(d.getDate() - 7)
    return d.toISOString()
  }
  if (preset === '30d') {
    const d = new Date(now)
    d.setDate(d.getDate() - 30)
    return d.toISOString()
  }
  return now.toISOString()
}

// ===================
// Component
// ===================

export function ActivitySection({ bidId }: ActivitySectionProps) {
  const currentUserId = useAuthStore((s) => s.user?.id ?? '')
  const { items, total, filters, isLoading, load, loadMore, setFilter } = useActivityStore()

  const [members, setMembers] = useState<BidMember[]>([])
  // Date filter stores a computed `from` date, so track the chosen preset separately
  // to keep the Date dropdown reflecting the selection.
  const [datePreset, setDatePreset] = useState('__all')

  // Load members for Person filter
  const fetchMembers = useCallback(async () => {
    try {
      const data = await api.get<{ members: BidMember[] }>(`/bids/${bidId}/members`)
      setMembers(data.members)
    } catch {
      // Non-fatal — Person dropdown just stays empty
    }
  }, [bidId])

  // Mount: load activity + members
  useEffect(() => {
    void load(bidId)
    void fetchMembers()
  }, [bidId, load, fetchMembers])

  // ---- Filter handlers ----

  const handleActorChange = (value: string) => {
    void setFilter('actor', value === '__all' ? undefined : value)
  }

  const handleTypeChange = (value: string) => {
    void setFilter('type', value === '__all' ? undefined : value)
  }

  const handleDateChange = (value: string) => {
    setDatePreset(value)
    if (value === '__all') {
      void setFilter('from', undefined)
    } else {
      void setFilter('from', getFromDate(value))
    }
  }

  const handleExport = async () => {
    try {
      await exportBidActivityCsv(bidId, filters)
    } catch {
      toast.error('Failed to export activity')
    }
  }

  // ---- Day grouping ----

  const groups: { key: string; label: string; items: typeof items }[] = []
  const seen = new Map<string, number>()

  for (const item of items) {
    const key = getDayKey(item.createdAt)
    const existingIdx = seen.get(key)
    if (existingIdx !== undefined) {
      groups[existingIdx]?.items.push(item)
    } else {
      seen.set(key, groups.length)
      groups.push({ key, label: getDayLabel(item.createdAt), items: [item] })
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Keep filters outside the scrolling history so rows cannot pass above them. */}
      <div role="group" aria-label="Activity filters" className="shrink-0 bg-background px-6 py-2 flex flex-wrap items-center gap-2">
        {/* Person filter */}
        <Select
          value={filters.actor ?? '__all'}
          onValueChange={handleActorChange}
        >
          <SelectTrigger aria-label="Person" className="h-8 w-auto min-w-[130px] text-sm">
            <SelectValue placeholder="All people" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All people</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.userId} value={m.userId}>
                {m.fullName || m.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Type filter */}
        <Select
          value={filters.type ?? '__all'}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger aria-label="Type" className="h-8 w-auto min-w-[130px] text-sm">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All types</SelectItem>
            {TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date filter */}
        <Select
          value={datePreset}
          onValueChange={handleDateChange}
        >
          <SelectTrigger aria-label="Time" className="h-8 w-auto min-w-[120px] text-sm">
            <SelectValue placeholder="All time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All time</SelectItem>
            {DATE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Export — right-aligned */}
        <div className="ml-auto">
          <Button variant="ghost" size="sm" onClick={() => void handleExport()}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Body */}
      <div
        role="region"
        aria-label="Activity history"
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {/* Loading skeleton — rendered here so the filter bar above stays mounted */}
        {isLoading && items.length === 0 && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <Skeleton className="h-6 w-6 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4 rounded" />
                  <Skeleton className="h-3 w-1/4 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state — flat, no card */}
        {!isLoading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Activity className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No activity yet. Actions on this bid will show up here.
            </p>
          </div>
        )}

        {/* Day-grouped rows */}
        {groups.map((group) => (
          <div key={group.key} className="mb-4">
            <p className="text-meta mb-2 select-none">{group.label}</p>
            <div>
              {group.items.map((item) => (
                <ActivityRow
                  key={item.id}
                  record={item}
                  currentUserId={currentUserId}
                  bidId={bidId}
                  // Relative time only under "Today", where the two agree.
                  // Elsewhere the heading names the day and a "27m ago" stamp
                  // measured across midnight would contradict it.
                  timestampFormat={group.label === 'Today' ? 'relative' : 'clock'}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Load more */}
        {items.length < total && (
          <div className="flex justify-center pt-2 pb-4">
            <Button
              variant="ghost"
              size="sm"
              disabled={isLoading}
              onClick={() => void loadMore(bidId)}
            >
              {isLoading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
